import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Button } from 'reactstrap';
import { unix } from 'moment';
import classNames from 'classnames';
import ReactHighcharts from 'react-highcharts';
import { array, object } from 'prop-types';
import { startSetDashboardCharts } from '../../actions/dashboard.js';
import Card from '../../components/Card/Card';
import { _toFixed } from '../../utils/utils';
import DashboardStatTable from './DashboardStatTable.js';
import { LocalStorage } from '@utils/utils';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { DARK_THEME, getEffectiveTheme } from '../../utils/themeStorage';

const isDarkUi = (profileTheme) => getEffectiveTheme(profileTheme) === DARK_THEME;

const CHART_COLORS = {
  light: {
    clicks: '#F6EBFF',
    leads: '#7200CC',
    cr: '#2DC912',
    chartBg: '#fff',
    tooltipBg: '#fff',
    axisLine: '#eee',
    axisLabel: 'var(--ds-text-subtext)',
  },
  dark: {
    clicks: '#382745',
    leads: '#A030F7',
    cr: '#39DB1D',
    chartBg: '#282828',
    tooltipBg: '#303030',
    axisLine: 'rgba(255,255,255,0.15)',
    axisLabel: 'var(--ds-text-subtext)',
  },
};

const getChartThemeColors = (profileTheme) =>
  isDarkUi(profileTheme) ? CHART_COLORS.dark : CHART_COLORS.light;

const chartConfig = {
  title: {
    text: null,
  },

  xAxis: {
    categories: [],
    crosshair: {
      color: 'var(--ds-chart-active)',
    },
    tickColor: 'transparent',
    labels: {
      style: {
        fontFamily: 'Roboto Flex, sans-serif',
        color: '#acacac',
        fontSize: '16px',
        lineHeight: '21px',
      },
    },
  },

  yAxis: [
    {
      //--- Clicks yAxis
      // tickInterval: 5000,
      gridLineWidth: 0,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      categories: null,
    },
    {
      //--- Leads yAxis
      gridLineWidth: 0,
      // tickInterval: 20,
      categories: null,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      opposite: true,
    },
    {
      //--- CR yAxis
      gridLineWidth: 0,
      // tickInterval: 0.005,
      categories: null,
      labels: {
        enabled: false,
      },
      title: {
        text: null,
      },
      opposite: true,
    },
  ],

  credits: {
    text: '',
    href: '',
  },

  legend: {
    enabled: false,
  },
  plotOptions: {
    column: {
      grouping: false,
      shadow: false,
      borderWidth: 0,
      states: {
        hover: {
          enabled: false,
        },
      },
    },
  },

  symbols: ['circle'],
  tooltip: {
    backgroundColor: '#fff',
    borderWidth: 0,
    borderRadius: 8,
    padding: 12,
    shadow: false,
    crosshairs: false,
    shared: true,
    useHTML: true,
    style: {
      color: '#fff',
      cursor: 'default',
      fontFamily: 'Roboto Flex, sans-serif',
      fontSize: '16px',
      lineHeight: '140%',
      whiteSpace: 'nowrap',
    },
  },
};

const getInterval = (period) => {
  switch (period) {
    case 'day':
      return 4;
    case 'week':
      return null;
    case 'month':
      return 4;
  }
};

const renderTooltipPluralizeLabel = ({ intl, id, values }) =>
  intl.formatMessage({ id }, { values });

const getFormattedTooltip = (data = {}, period = 'week', timezone, indicator, intl, statistic) => {
  let hours;
  let tooltip;
  const formatted = unix(data.x).tz(timezone);

  if (period === 'day') {
    let hour = formatted.clone();
    let nextHour = formatted.clone().add(1, 'h');
    hours = `${hour.format('HH:mm')} &ndash; ${nextHour.format('HH:mm')}`;
  }

  if (data.points.length > 2 && statistic === 'general') {
    const [clicks, leads, cr] = data.points;
    const formattedCr = cr.y.toFixed(2);

    tooltip = `<div class="statitics-tooltip">
                <span class="statitics-tooltip-header">
                  ${formatted.format('DD MMM')}
                </span>

                ${period === 'day' ? `<span class="statitics-tooltip-sub">${hours}</span>` : ''}

                <ul class="statitics-tooltip-values">
                  <li>
                    <span class="statitics-tooltip-color" style="color:${
                      clicks.color
                    }">\u25CF</span>
                     ${renderTooltipPluralizeLabel({
                       intl,
                       id: 'widgets.statistics.tooltip.clicks',
                       values: clicks.y,
                     })}
                  </li>
                  <li>
                    <span class="statitics-tooltip-color" style="color:${leads.color}">\u25CF</span>
                     ${renderTooltipPluralizeLabel({
                       intl,
                       id: 'widgets.statistics.tooltip.leads',
                       values: leads.y,
                     })}
                  </li>
                  <li>
                    <span class="statitics-tooltip-color" style="color:${cr.color}">\u25CF</span>
                     ${formattedCr}%
                  </li>
                </ul>
              </div>`;
  } else if (data.points.length > 2 && statistic === 'SP') {
    const [clicks, leads, cr] = data.points;
    const formattedCr = cr.y.toFixed(2);

    tooltip = `<div class="statitics-tooltip">
                <span class="statitics-tooltip-header">
                  ${formatted.format('DD MMM')}
                </span>

                ${period === 'day' ? `<span class="statitics-tooltip-sub">${hours}</span>` : ''}

                <ul class="statitics-tooltip-values">
                  <li>
                    <span class="statitics-tooltip-color" style="color:${
                      clicks.color
                    }">\u25CF</span>
                     ${renderTooltipPluralizeLabel({
                       intl,
                       id: 'widgets.statistics.tooltip.clicks',
                       values: clicks.y,
                     })}
                  </li>
                  <li>
                    <span class="statitics-tooltip-color" style="color:${leads.color}">\u25CF</span>
                     ${renderTooltipPluralizeLabel({
                       intl,
                       id: 'widgets.statistics.tooltip.bills',
                       values: leads.y,
                     })}
                  </li>
                  <li>
                    <span class="statitics-tooltip-color" style="color:${cr.color}">\u25CF</span>
                     ${formattedCr}%
                  </li>
                </ul>
              </div>`;
  } else if (data.points.length === 2 && indicator) {
    const [points, cr] = data.points;
    const label = indicator.id;

    const formattedCr = cr.y.toFixed(2);

    tooltip = `<div class="statitics-tooltip">
              <span class="statitics-tooltip-header">${formatted.format('DD MMM')}</span>
              ${period === 'day' ? `<span class="statitics-tooltip-sub">${hours}</span>` : ''}
              <ul class="statitics-tooltip-values">
                <li>
                  <span class="statitics-tooltip-color" style="color:${points.color}">\u25CF</span>
                  ${renderTooltipPluralizeLabel({
                    intl,
                    id: `widgets.statistics.tooltip.${label}`,
                    values: points.y,
                  })}
                </li>
                <li>
                  <span class="statitics-tooltip-color" style="color:${cr.color}">\u25CF</span>
                  ${formattedCr}%
                </li>
              </ul>
            </div>`;
  }

  return tooltip;
};

class StatisticsChartWidget extends Component {
  static propTypes = {
    charts: array.isRequired,
    totalCharts: object.isRequired,
    intl: intlShape.isRequired,
  };

  static defaultProps = {
    charts: [],
    totalCharts: {},
  };

  constructor(props) {
    super(props);
    const themeColors = getChartThemeColors(props.theme);
    this.state = {
      loading: false,
      period: 'week',
      periods: ['day', 'week', 'month'],
      periodDropdownOpen: false,
      statistic: 'general',
      statisticTypes: ['general', 'SP'],
      data: {
        series: [],
        tooltip: {
          ...chartConfig.tooltip,
          backgroundColor: themeColors.tooltipBg,
        },
        xAxis: {
          ...chartConfig.xAxis,
          lineColor: themeColors.axisLine,
          labels: {
            ...chartConfig.xAxis.labels,
            style: {
              ...chartConfig.xAxis.labels.style,
              color: themeColors.axisLabel,
            },
          },
        },
        yAxis: {
          ...chartConfig.yAxis,
        },
        chart: {
          backgroundColor: themeColors.chartBg,
          type: 'column',
          height: 248,
          ...chartConfig.chart,
        },
      },
      indicators: [
        { id: 'clicks', selected: true },
        { id: 'leads', selected: true },
        { id: 'cr', selected: true },
      ],
      spIndicators: [
        { id: 'clicks', selected: true },
        { id: 'bills', selected: true },
        { id: 'cr', selected: true },
      ],
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.theme !== nextProps.theme) {
      this.changeChartType();
      this.changeChartColors(nextProps);
      return;
    }

    this.formatSeries(nextProps.charts);
  }

  changeChartType = () => {
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        chart: {
          ...prevState.data.chart,
          type: 'column',
        },
      },
    }));
  };

  changeChartColors = (nextProps = this.props) => {
    const themeColors = getChartThemeColors(nextProps.theme);
    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        chart: {
          ...prevState.data.chart,
          backgroundColor: themeColors.chartBg,
        },
        xAxis: {
          ...prevState.data.xAxis,
          lineColor: themeColors.axisLine,
          labels: {
            ...prevState.data.xAxis.labels,
            style: {
              ...(prevState.data.xAxis.labels && prevState.data.xAxis.labels.style),
              color: themeColors.axisLabel,
            },
          },
        },
        tooltip: {
          ...prevState.data.tooltip,
          backgroundColor: themeColors.tooltipBg,
        },
      },
    }));
    this.formatSeries(nextProps.charts || this.props.charts);
  };

  componentDidMount() {
    this.getStatsData(this.getStatPeriod(), this.getStatCategory());
    this.lastThemeClass = isDarkUi(this.props.theme) ? 'theme-2' : 'theme-1';
    this.themeObserver = new MutationObserver(() => {
      const nextThemeClass = document.documentElement.classList.contains('theme-2')
        ? 'theme-2'
        : 'theme-1';
      if (nextThemeClass === this.lastThemeClass) {
        return;
      }
      this.lastThemeClass = nextThemeClass;
      this.changeChartColors();
    });
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  componentWillUnmount() {
    if (this.themeObserver) {
      this.themeObserver.disconnect();
    }
  }

  renderControls = (type) => {
    const { periods, statisticTypes } = this.state;

    switch (type) {
      case 'period':
        return periods.map((period) => (
          <a
            key={period}
            role="button"
            className={classNames('nav-link', { active: this.state.period === period })}
            onClick={() => this.changePeriod(period)}
          >
            <FormattedMessage id={`widgets.statistics.periods.${period}`} defaultMessage={period} />
          </a>
        ));
        break;
      case 'statistics':
        return statisticTypes.map((statistic) => (
          <a
            key={statistic}
            role="button"
            className={classNames('nav-link', { active: this.state.statistic === statistic })}
            onClick={() => this.changeStatistic(statistic)}
          >
            <FormattedMessage
              id={`widgets.statistics.statistic.${statistic}`}
              defaultMessage={statistic}
            />
          </a>
        ));
        break;
      default:
        break;
    }
  };

  renderPeriodDropdown = () => {
    const { periods, period, periodDropdownOpen } = this.state;

    return (
      <Dropdown
        isOpen={periodDropdownOpen}
        toggle={() => this.setState({ periodDropdownOpen: !periodDropdownOpen })}
      >
        <DropdownToggle tag="span" caret>
          <FormattedMessage id={`widgets.statistics.periods.${period}`} defaultMessage={period} />
        </DropdownToggle>
        <DropdownMenu>
          {periods.map((periodOption) => (
            <DropdownItem
              key={periodOption}
              onClick={() => this.changePeriod(periodOption)}
              active={period === periodOption}
            >
              <FormattedMessage
                id={`widgets.statistics.periods.${periodOption}`}
                defaultMessage={periodOption}
              />
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    );
  };

  changePeriod = (period) => {
    this.setState(
      () => ({ period }),
      () => {
        this.getStatsData(this.getStatPeriod(), this.getStatCategory());
      },
    );
  };

  changeStatistic = (statistic) => {
    LocalStorage.set('StatisticsChartWidget', statistic);
    this.setState(
      () => ({ statistic }),
      () => {
        this.getStatsData(this.getStatPeriod(), this.getStatCategory());
      },
    );
  };

  toggleIndicator = (id, type) => {
    if (id === 'cr') return;

    type === 'general' &&
      this.setState(
        (prevState) => ({
          indicators: prevState.indicators.map((indicator, index) => {
            if (indicator.id === id) {
              return {
                ...indicator,
                selected: !prevState.indicators[index].selected,
              };
            } else {
              return indicator;
            }
          }),
        }),
        () => {
          this.formatSeries(this.props.charts);
        },
      );

    type === 'SP' &&
      this.setState(
        (prevState) => ({
          spIndicators: prevState.spIndicators.map((indicator, index) => {
            if (indicator.id === id) {
              return {
                ...indicator,
                selected: !prevState.spIndicators[index].selected,
              };
            } else {
              return indicator;
            }
          }),
        }),
        () => {
          this.formatSeries(this.props.charts);
        },
      );
  };

  getStatPeriod = () => this.state.period;

  getStatCategory = () => this.state.statistic;

  getChartConfig = () => {
    const config = {
      ...chartConfig,
      ...this.state.data,
      yAxis: chartConfig.yAxis,
    };
    return config;
  };

  formatSeries = (data = []) => {
    const { indicators, period, statistic, spIndicators } = this.state;
    const { timezone, theme, intl } = this.props;
    const themeColors = getChartThemeColors(theme);
    const seriesBorderWidth = isDarkUi(theme) ? 0 : 1;

    const series = [
      {
        yAxis: 0,
        name: 'Clicks',
        data: [],
        color: themeColors.clicks,
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: seriesBorderWidth,
        borderRadius: data.length < 8 ? 8 : 4,
      },
      {
        yAxis: 0,
        name: 'Leads',
        data: [],
        color: themeColors.leads,
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: seriesBorderWidth,
      },
      {
        yAxis: 2,
        name: 'CR',
        data: [],
        color: themeColors.cr,
        pointPadding: 0,
        groupPadding: 0,
        borderWidth: seriesBorderWidth,
      },
    ];

    const categories = [];
    const format = period === 'day' ? 'HH:mm' : 'DD MMM';
    const tickInterval = getInterval(period);
    const isClicks = statistic === 'general' ? indicators[0].selected : spIndicators[0].selected;
    const isLeads = statistic === 'general' ? indicators[1].selected : spIndicators[1].selected;
    const isCR = statistic === 'general' ? indicators[2].selected : spIndicators[2].selected;
    const currentIndicator = this.currentIndicator;

    data.length > 0 &&
      data.forEach((item) => {
        categories.push(item[0]);
        isClicks && series[0].data.push(item[2]);
        isLeads && series[1].data.push(item[3]);
        isCR && series[2].data.push(item[1]);
      });

    this.setState((prevState) => ({
      data: {
        ...prevState.data,
        series,
        tooltip: {
          ...prevState.data.tooltip,
          formatter: function () {
            return getFormattedTooltip(this, period, timezone, currentIndicator, intl, statistic);
          },
        },

        xAxis: {
          ...prevState.data.xAxis,
          categories,
          tickInterval,
          labels: {
            ...prevState.data.xAxis.labels,
            formatter: function () {
              return unix(this.value).tz(timezone).format(format);
            },
          },
        },
      },
    }));
  };

  getStatsData = (period, category) => {
    this.props.startSetDashboardCharts({ period, category: category ?? '' });
  };

  getTotalCharts = (key) => {
    if (key === 'cr' && this.props.totalCharts[key] >= 0) {
      return _toFixed(this.props.totalCharts[key], 2) + '%';
    }

    return this.props.totalCharts[key] || 0;
  };

  get currentIndicator() {
    return this.state.statistic === 'general'
      ? this.state.indicators.find(({ selected }) => selected)
      : this.state.spIndicators.find(({ selected }) => selected);
  }

  render() {
    const { loading, indicators, period, statistic, spIndicators } = this.state;
    const { collapsing, localStorageName, theme } = this.props;

    return (
      <Card
        className="statistics-widget"
        loading={loading}
        defaultMessage="Statistics"
        localStorageName={localStorageName}
        collapsing={collapsing}
      >
        <Card.Header>
          <p className="statistics-widget-title">Статистика</p>

          {this.renderPeriodDropdown()}
        </Card.Header>
        <Card.Body>
          <ReactHighcharts config={this.getChartConfig()} ref="chart" />
          <ul className="legend list-inline">
            {statistic === 'general' &&
              indicators.map(({ id, selected }) => (
                <li className="list-inline-item" key={id}>
                  <Button
                    color="link"
                    size="sm"
                    // onClick={() => this.toggleIndicator(id, statistic)}
                  >
                    <span
                      className={classNames(`indicator indicator-${id}`, { inactive: !selected })}
                    >
                      <FormattedMessage
                        id={`widgets.statistics.indicators.${id}`}
                        defaultMessage={id}
                      />
                      &nbsp;&mdash; {this.getTotalCharts(id)}
                    </span>
                  </Button>
                </li>
              ))}
          </ul>
        </Card.Body>
        <Card.Body className="dashboard-stat-table">
          <DashboardStatTable period={period} statistic={statistic} />
        </Card.Body>
      </Card>
    );
  }
}

const mapStateToProps = ({ dashboard, webmaster }) => {
  const fixtures = {
    charts: [
      [1571518800, 0.07, 0, 0],
      [1571605200, 0, 0, 0],
      [1571691600, 0, 3, 0],
      [1571778000, 0.035, 0, 0],
      [1571864400, 0.035, 1, 0],
      [1571950800, 0, 1, 0],
    ],
    totalCharts: { cr: 0, clicks: 5, leads: 0 },
  };

  return {
    // charts: fixtures.charts,
    charts: dashboard.charts,
    // totalCharts: fixtures.totalCharts,
    totalCharts: dashboard.totalCharts,
    timezone: webmaster.profile.timezone,
    theme: webmaster.profile.theme,
  };
};

const mapDispatchToProps = (dispatch) => ({
  startSetDashboardCharts: (params) => dispatch(startSetDashboardCharts(params)),
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(StatisticsChartWidget));
